import { language, type Language } from '../../i18n';
import { isCompleteTranslation, type ContentTranslation, type ContentTranslations } from '../../i18n/content';

interface Props {
  kind: 'blog' | 'project';
  sourceLanguage: Language;
  translations: ContentTranslations;
  onSourceLanguageChange: (language: Language) => void;
  onChange: (translations: ContentTranslations) => void;
}

export default function ContentTranslationsEditor({ kind, sourceLanguage, translations, onSourceLanguageChange, onChange }: Props) {
  const fr = language === 'fr';
  const target: Language = sourceLanguage === 'fr' ? 'en' : 'fr';
  const translation = translations[target] || {};
  const update = (field: keyof ContentTranslation, value: string | string[] | number) => {
    onChange({ ...translations, [target]: { ...translation, [field]: value } });
  };
  const summaryField = kind === 'blog' ? 'excerpt' : 'description';

  return (
    <section className="admin-panel-muted p-5 space-y-5">
      <h2 className="admin-section-title">{fr ? 'Versions FR / EN' : 'FR / EN versions'}</h2>
      <div>
        <label htmlFor="source-language" className="label">{fr ? 'Langue du texte original ci-dessus' : 'Language of the original text above'}</label>
        <select id="source-language" className="input" value={sourceLanguage} onChange={event => onSourceLanguageChange(event.target.value as Language)}>
          <option value="en">English (EN)</option>
          <option value="fr">Français (FR)</option>
        </select>
      </div>
      <h3 className="font-semibold">{fr ? 'Traduction' : 'Translation'} — {target === 'fr' ? 'Français (FR)' : 'English (EN)'}</h3>
      <p className="text-sm text-foreground/60" role="status">
        {isCompleteTranslation(translation, kind)
          ? (fr ? 'Cette version sera affichée aux visiteurs de cette langue après enregistrement.' : 'This version will be shown to visitors using this language after saving.')
          : (fr ? 'Renseignez le titre, le résumé et le contenu pour afficher cette traduction. En attendant, le texte original reste affiché.' : 'Fill in the title, summary and content to display this translation. Until then, the original text is shown.')}
      </p>
      <div>
        <label htmlFor={`translation-${target}-title`} className="label">{fr ? 'Titre traduit' : 'Translated title'}</label>
        <input id={`translation-${target}-title`} lang={target} className="input" maxLength={255} value={translation.title || ''} onChange={event => update('title', event.target.value)} />
      </div>
      <div>
        <label htmlFor={`translation-${target}-summary`} className="label">{fr ? 'Résumé traduit' : 'Translated summary'}</label>
        <textarea id={`translation-${target}-summary`} lang={target} className="input" rows={4} value={translation[summaryField] || ''} onChange={event => update(summaryField, event.target.value)} />
      </div>
      <div>
        <label htmlFor={`translation-${target}-content`} className="label">{fr ? 'Contenu traduit (Markdown)' : 'Translated content (Markdown)'}</label>
        <textarea id={`translation-${target}-content`} lang={target} className="input font-mono text-sm leading-6" rows={20} value={translation.content || ''} onChange={event => update('content', event.target.value)} />
      </div>
      {kind === 'blog' ? (
        <>
          <div>
            <label htmlFor={`translation-${target}-categories`} className="label">{fr ? 'Catégories traduites (séparées par des virgules)' : 'Translated categories (comma-separated)'}</label>
            <input id={`translation-${target}-categories`} lang={target} className="input" value={(translation.categories || []).join(',')} onChange={event => update('categories', event.target.value.split(','))} />
          </div>
          <div>
            <label htmlFor={`translation-${target}-tags`} className="label">{fr ? 'Mots-clés traduits (séparés par des virgules)' : 'Translated tags (comma-separated)'}</label>
            <input id={`translation-${target}-tags`} lang={target} className="input" value={(translation.tags || []).join(',')} onChange={event => update('tags', event.target.value.split(','))} />
          </div>
        </>
      ) : (
        <div>
          <label htmlFor={`translation-${target}-category`} className="label">{fr ? 'Catégorie traduite' : 'Translated category'}</label>
          <input id={`translation-${target}-category`} lang={target} className="input" maxLength={100} value={translation.category || ''} onChange={event => update('category', event.target.value)} />
        </div>
      )}
      <p className="text-xs text-foreground/50">{fr ? 'L’adresse, les images et les liens sont communs aux deux versions. Les champs ci-dessus sont enregistrés avec le bouton de sauvegarde de la page.' : 'The URL, images and links are shared by both versions. Use the page’s save button to save these fields.'}</p>
    </section>
  );
}
